import React, { useState } from 'react';
import { billingService } from '../../services/billingService';
import { STRIPE_PUBLISHABLE_KEY } from '../../config/env';
import './PaymentMethodForm.css';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

export default function PaymentMethodForm({ onSuccess, onError, submitLabel = 'Add Card' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const cardElementRef = React.useRef(null);
  const stripeRef = React.useRef(null);
  const elementsRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Initialize Stripe Elements on mount
  React.useEffect(() => {
    if (!window.Stripe) {
      setError(getUserFacingError(new Error('Stripe.js not loaded.'), 'payment-method'));
      return;
    }
    if (!STRIPE_PUBLISHABLE_KEY) {
      setError(getUserFacingError(new Error('Stripe publishable key not configured.'), 'payment-method'));
      return;
    }

    const stripe = window.Stripe(STRIPE_PUBLISHABLE_KEY);
    stripeRef.current = stripe;

    const elements = stripe.elements({
      fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap' }],
    });
    elementsRef.current = elements;

    // Read current theme colors from CSS variables (Stripe Elements can't use CSS vars directly)
    const cs = getComputedStyle(document.documentElement);
    const textColor = cs.getPropertyValue('--text-primary').trim() || '#0B0C0E';
    const placeholderColor = cs.getPropertyValue('--text-tertiary').trim() || '#71717a';

    const cardElement = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          fontFamily: 'Inter, sans-serif',
          color: textColor,
          '::placeholder': { color: placeholderColor },
        },
        invalid: { color: '#ef4444' },
      },
    });

    cardElement.mount(containerRef.current);
    cardElement.on('change', (event) => {
      setCardComplete(event.complete);
      setError(event.error ? getUserFacingError(event.error, 'payment-method') : null);
    });

    cardElementRef.current = cardElement;

    return () => {
      cardElement.destroy();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripeRef.current || !cardElementRef.current) return;

    setLoading(true);
    setError(null);

    try {
      // Create PaymentMethod with Stripe
      const { paymentMethod, error: stripeError } = await stripeRef.current.createPaymentMethod({
        type: 'card',
        card: cardElementRef.current,
      });

      if (stripeError) {
        setError(getUserFacingError(stripeError, 'payment-method'));
        setLoading(false);
        return;
      }

      // Attach to backend
      await billingService.addPaymentMethod(paymentMethod.id);
      onSuccess?.(paymentMethod);
    } catch (err) {
      setError(getUserFacingError(err, 'payment-method'));
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      <div className="payment-field-header">
        <label className="payment-label">Card Details</label>
      </div>
      <div className="payment-card-field">
        <div className="card-element-wrapper" ref={containerRef} />
        <div className="payment-card-meta">
          <span>Visa</span>
          <span>Mastercard</span>
          <span>Amex</span>
        </div>
      </div>
      {error && <InlineAlert compact title={error.title} message={error.message} />}
      <button
        type="submit"
        className="payment-submit-btn"
        disabled={loading || !cardComplete}
      >
        {loading ? 'Processing...' : submitLabel}
      </button>
    </form>
  );
}
